<template>
  <div class="pa-6">
    <!-- Breadcrumb and back button -->
    <v-row class="align-center bread-row">
      <v-col cols="12" md="10">
        <Breadcrump :crumbs="crumbs" />
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-end">
        <v-btn @click="$router.go(-1)" fab small dark color="#1976d2">
          <v-icon dark>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Request Form -->
    <v-card v-if="hasPerm('DeviceDb_Request')" class="pa-4 elevation-3 mt-4">
      <v-card-title
        class="title-font d-flex justify-space-between align-center"
      >
        درخواست داده از دیتابیس دستگاه
      </v-card-title>

      <v-row class=" calendar-row">
        <v-col cols="12" md="5">
          <div class="calendar-wrapper">
            <p class="calendar-label">از:</p>
            <date-picker
              simple
              editable
              required
              v-model="form.startDate"
              placeholder="انتخاب تاریخ"
            />
          </div>
        </v-col>
        <v-col cols="12" md="5">
          <div class="calendar-wrapper">
            <p class="calendar-label">تا:</p>
            <date-picker
              simple
              editable
              required
              v-model="form.endDate"
              placeholder="انتخاب تاریخ"
            />
          </div>
        </v-col>
      </v-row>

      <v-row class="px-4 pb-2">
        <v-col cols="12" md="5">
          <v-select
            v-model="form.tableName"
            :items="tableNames"
            label="نام جدول"
            outlined
            dense
            hide-details
            :menu-props="{ bottom: true, offsetY: true }"
          ></v-select>
        </v-col>
      </v-row>
      <v-card-actions class="px-4">
        <v-btn
          color="primary"
          @click="requestData"
          :loading="isRequesting"
          :disabled="isRequesting"
        >
          <v-icon left small>mdi-database-export</v-icon>
          {{ isRequesting ? "در حال ارسال..." : "ارسال درخواست" }}
        </v-btn>
      </v-card-actions>
      <v-alert v-if="isSending" type="info" text prominent class="mt-3 mx-4">
        درخواست قبلی شما ثبت شده و در انتظار دریافت توسط دستگاه است. دستگاه تا
        <strong>۳ دقیقه</strong> فرصت دارد این درخواست را دریافت کند. لطفاً تا
        پایان این زمان از ارسال درخواست جدید خودداری کنید.
      </v-alert>
    </v-card>

    <!-- Existing Files -->
    <v-card v-if="hasPerm('DeviceDb_Show')" class="pa-4 elevation-3 mt-4">
      <v-card-title
        class="title-font d-flex justify-space-between align-center"
      >
        فایل‌های موجود
        <div>
          <v-btn
            color="green lighten-1"
            outlined
            small
            @click="loadFiles"
            :loading="loading"
          >
            <v-icon left small>mdi-refresh</v-icon>
            بروزرسانی
          </v-btn>
        </div>
      </v-card-title>

      <v-data-table
        :loading="loading"
        :headers="fileHeaders"
        :items="files"
        dense
        class="elevation-1"
        :item-class="item => (item.error ? 'error-row' : '')"
        :items-per-page="10"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-arrow-left',
          nextIcon: 'mdi-arrow-right',
          itemsPerPageOptions: [5, 10, 25, 50]
        }"
        no-data-text="فایلی برای این دستگاه وجود ندارد"
      >
        <template v-slot:item.uploadedAt="{ item }">
          {{ formatJalaliDate(item.uploadedAt) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            :href="item.fileUrl"
            target="_blank"
            small
            color="primary"
            outlined
          >
            <v-icon left small>mdi-download</v-icon>
            دانلود
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Message Alert -->
    <v-snackbar
      v-model="showMessage"
      :color="isError ? 'error' : 'success'"
      top
      center
    >
      {{ message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showMessage = false">
          بستن
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment-jalaali";
import request from "@/utils/request";
import { hasPermission } from "@/composables/permissions";
import Breadcrump from "@/components/common/Breadcrump.vue";

export default {
  name: "DeviceDb",
  components: { Breadcrump },
  data() {
    return {
      crumbs: [
        { text: "خانه", disabled: false, href: "/" },
        { text: "مجموعه ها", disabled: false, href: "/complexes" },
        {
          text: `مجموعه ${this.$store.state.selectedComplexName || ""}`,
          disabled: false,
          href: `/complex-units/${this.$store.state.selectedComplexId}`
        },
        {
          text: this.$store.state.selectedDeviceName || "دستگاه",
          href: `/complex-devices/${this.$store.state.selectedComplexId}`,
          disabled: false
        },
        { text: "دیتابیس دستگاه", disabled: true }
      ],
      tableNames: ["Admins", "Personnels", "lockers", "lockersState", "logs"],
      form: {
        tableName: null,
        startDate: null,
        endDate: null
      },
      startDateMenu: false,
      endDateMenu: false,
      isRequesting: false,
      files: [],
      isSending: false,
      loading: false,
      message: "",
      isError: false,
      showMessage: false,
      fileHeaders: [
        { text: "نام فایل", value: "fileName" },
        { text: "تاریخ آپلود", value: "uploadedAt" },
        { text: "خطا", value: "error", sortable: false },
        { text: "عملیات", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    deviceId() {
      return Number(this.$route.params.id);
    },
    startDateFormatted() {
      return this.form.startDate
        ? moment(this.form.startDate, "jYYYY/jMM/jDD").format(
            "YYYY/MM/DDT00:00:00"
          )
        : "";
    },
    endDateFormatted() {
      return this.form.endDate
        ? moment(this.form.endDate, "jYYYY/jMM/jDD").format(
            "YYYY/MM/DDT00:00:00"
          )
        : "";
    }
  },
  created() {
    if (this.hasPerm("DeviceDb_Show")) {
      this.loadFiles();
    }
  },
  methods: {
    hasPerm(item) {
      return hasPermission(item);
    },
    async loadFiles() {
      this.loading = true;
      try {
        const response = await request.get(`/device/Db/${this.deviceId}`);
        if (response.data.result) {
          this.isSending = response.data.result.isSending;
          this.files =
            response.data.result.files &&
            Array.isArray(response.data.result.files)
              ? response.data.result.files
              : [];
        } else {
          this.files = [];
        }
      } catch (error) {
        this.files = [];
        this.showNotification("خطا در دریافت لیست فایل‌ها", true);
      } finally {
        this.loading = false;
      }
    },
    async requestData() {
      // Validation
      if (!this.form.tableName) {
        this.showNotification("لطفاً یک جدول انتخاب کنید", true);
        return;
      }
      if (!this.form.startDate || !this.form.endDate) {
        this.showNotification("تاریخ شروع و پایان الزامی است", true);
        return;
      }

      this.isRequesting = true;
      try {
        const payload = {
          deviceId: this.deviceId,
          tableName: this.form.tableName,
          startTimeFilter: this.startDateFormatted,
          endTimeFilter: this.endDateFormatted
        };

        await request.post(`/device/Db`, payload);

        this.showNotification(
          "درخواست شما با موفقیت ثبت شد و اکنون در صف دریافت توسط دستگاه قرار دارد. " +
            "دستگاه تا ۳ دقیقه آینده فرصت دارد این درخواست را دریافت کند. " +
            "پس از دریافت، فایل نهایی ساخته و در جدول زیر نمایش داده خواهد شد.",
          false
        );

        // Refresh file list after delay
        setTimeout(() => {
          if (this.hasPerm("DeviceDb_Show")) {
            this.loadFiles();
          }
        }, 2500);
      } catch (error) {
        this.showNotification(
          "خطا در ثبت درخواست. لطفاً دوباره تلاش کنید",
          true
        );
      } finally {
        this.isRequesting = false;
      }
    },
    showNotification(message, isError = false) {
      this.message = message;
      this.isError = isError;
      this.showMessage = true;
    },
    formatJalaliDate(datetime) {
      if (!datetime) return "-";
      try {
        return moment(datetime).format("jYYYY/jMM/jDD HH:mm:ss");
      } catch {
        return "-";
      }
    }
  }
};
</script>

<style scoped>
.pa-6 {
  padding: 4px !important;
  min-height: 100vh;
}

.bread-row {
  margin-bottom: 4px !important;
  margin-top: -8px !important;
}

.v-card {
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.2s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.v-card-title {
  font-weight: 600;
  font-size: 17px !important;
  color: #1a1a1a;
}

.title-font {
  font-family: "IranSans", sans-serif !important;
  font-weight: 600 !important;
}

.v-data-table {
  font-size: 13.5px;
  border-radius: 12px;
  overflow: hidden;
}

::v-deep .error-row {
  background-color: #ffebee !important;
}

::v-deep .error-row:hover {
  background-color: #ffcdd2 !important;
}

.v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600;
}
.calendar-row {
  margin-block: 0px !important;
  padding-inline: 12px;
  gap: 16px;
  flex-wrap: wrap;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
}

.calendar-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

@media (max-width: 600px) {
  .pa-6 {
    padding: 12px !important;
  }
  .v-card-title {
    font-size: 16px !important;
  }
}
</style>
